import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './ModalEditorHistory.css';
import { getDashboardHistoryLogs, getLevelHistoryLogs, getExamHistoryLogs, getTestHistoryLogs, getChapterHistoryLogs } from '../../api/history_logs';

function ModalEditorHistory({ dashboard, levelId, examId, testId, chapterId, isOpen, closeModal }) {
  const { t } = useTranslation();
  const [historyLogs, setHistoryLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);

  // Fetch history logs when modal opens
  useEffect(() => {
    if (isOpen && (chapterId || levelId || examId || testId || dashboard)) {
      fetchHistoryLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, chapterId]);

  const fetchHistoryLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      let logs = [];
      if (dashboard) {
        logs = await getDashboardHistoryLogs();
        setHistoryLogs(logs);
      } else if (levelId) {
        logs = await getLevelHistoryLogs(levelId);
        setHistoryLogs(logs);
      } else if (examId) {
        logs = await getExamHistoryLogs(examId);
        setHistoryLogs(logs);
      }
      else if (testId) {
        logs = await getTestHistoryLogs(testId);
        setHistoryLogs(logs);
      }
      else if (chapterId) {
        logs = await getChapterHistoryLogs(chapterId);
        setHistoryLogs(logs);
      }
      if (logs.length > 0) {
        setSelectedLog(logs[0]);
      }
    } catch (err) {
      console.error('Error fetching history logs:', err);
      setError(t('modalEditorHistory.failedLoadHistory'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isOpen) return null;

  return (
    <div className="Modal ModalEditorHistory">
      <div className="history-modal-container">
        <div className="history-modal-header">
          <h2>{t('modalEditorHistory.changeHistoryLog')}</h2>
          <button className="close-btn" onClick={closeModal} title={t('common.close')}>✕</button>
        </div>

        <div className="history-modal-content">
          <div className="history-logs-list">
            <h3>{t('modalEditorHistory.recentChanges')}</h3>
            {loading && <p className="loading-text">{t('modalEditorHistory.loadingHistory')}</p>}
            {error && <p className="error-text">{error}</p>}

            {!loading && historyLogs.length === 0 && (
              <p className="empty-text">{t('modalEditorHistory.noHistoryLogs')}</p>
            )}

            {!loading && historyLogs.length > 0 && (
              <div className="logs-container">
                {historyLogs.map((log) => (
                  <div
                    key={log.id}
                    className={`history-item ${selectedLog?.id === log.id ? 'active' : ''}`}
                    onClick={() => setSelectedLog(log)}
                  >
                    <div className="history-item-header">
                      <span className="log-title">{log.log_title}</span>
                    </div>
                    <div className="history-item-meta">
                      <span className="user-name">{t('modalEditorHistory.byUser', { username: log.user_username })}</span>
                      <span className="log-date">{formatDate(log.log_date)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="history-details-panel">
            <h3>{t('modalEditorHistory.details')}</h3>
            {selectedLog ? (
              <div className="details-content">
                <div className="detail-section">
                  <label>{t('modalEditorHistory.action')}</label>
                  <span className="detail-value">{selectedLog.log_title}</span>
                </div>

                <div className="detail-section">
                  <label>{t('modalEditorHistory.dateTime')}</label>
                  <span className="detail-value">{formatDate(selectedLog.log_date)}</span>
                </div>

                <div className="detail-section">
                  <label>{t('modalEditorHistory.modifiedBy')}</label>
                  <span className="detail-value">{selectedLog.user_username}</span>
                </div>

                {selectedLog.log_data && Object.keys(selectedLog.log_data).length > 0 && (
                  <div className="detail-section">
                    <label>{t('modalEditorHistory.additionalInfo')}</label>
                    <div className="detail-data">
                      <pre>{JSON.stringify(selectedLog.log_data, null, 2)}</pre>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="empty-details">{t('modalEditorHistory.selectEntryPrompt')}</p>
            )}
          </div>
        </div>

        <div className="history-modal-footer">
          <button className="btn-refresh" onClick={fetchHistoryLogs} title={t('modalEditorHistory.refreshTooltip')}>
            {t('modalEditorHistory.refresh')}
          </button>
          <button className="btn-close" onClick={closeModal}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ModalEditorHistory;
